/**
 * @fileoverview One B2B event card on the events list.
 * @module pages/b2bEvents/B2BEventCard
 */

import { useCallback } from "react";
import dayjs from "dayjs";
import { generateAssetUrl, truncateText } from "../../utils/common.js";
import AssetImage from "../../components/AssetImage";
import { isPastEvent } from "../../utils/pastEventsAccess.js";
import { showBuyerOwnSchedulesButtonForEvent } from "../../utils/b2bEventVirtualLineSchedules";
import { ReactComponent as CalendarIcon } from "../../assets/icons/Calendar_Today.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/Edit.svg";
import { ReactComponent as CompaniesIcon } from "../../assets/icons/B2BEvents/CompaniesIcon.svg";
import Caintra_Logo_03 from "../../assets/Caintra_Logo_03.png";
import EventBanner from "../../assets/EventBanner.png";
import styles from "../../styles/B2BEvents.module.css";
import { DESCRIPTION_PREVIEW_LENGTH } from "./b2bEventsLayout";

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const EventDateColumn = ({ value }) => (
  <div className={styles.eventDate}>
    <span className={styles.dayOfWeek}>{dayjs(value).utc().format("dddd")}</span>
    <span className={styles.day}>{dayjs(value).utc().format("DD")}</span>
    <span className={styles.monthAndYear}>
      {dayjs(value).utc().format("MMMM")}
      {", "}
      {dayjs(value).utc().format("YYYY")}
    </span>
  </div>
);

/**
 * Staff and buyer links under the event description.
 * @param {Object} props
 * @returns {JSX.Element|null}
 */
const EventHub = ({
  t,
  showHub,
  showVirtualLineCompaniesButton,
  showBuyerSchedules,
  openCompanies,
  openSchedules,
}) => {
  if (!showHub) {
    return null;
  }
  return (
    <div className={`${styles.virtualLineHubRow} ${styles.virtualLineHubRowInDescription}`}>
      {showVirtualLineCompaniesButton && (
        <button type="button" className={styles.virtualLineHubButton} onClick={openCompanies}>
          <CompaniesIcon className={styles.virtualLineHubButtonIcon} />
          {t("pages.b2bEvents.viewParticipatingCompaniesList")}
        </button>
      )}
      {showBuyerSchedules && (
        <button type="button" className={styles.virtualLineHubButton} onClick={openSchedules}>
          <CompaniesIcon className={styles.virtualLineHubButtonIcon} />
          {t("pages.b2bEvents.buyerSchedulesList")}
        </button>
      )}
    </div>
  );
};

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const B2BEventCard = ({
  event,
  t,
  user,
  navigate,
  canManageStaff,
  showVirtualLineCompaniesButton,
  onOpenSchedules,
}) => {
  const editEvent = useCallback(() => {
    navigate("/b2bEventForm", {
      state: { isEditMode: true, eventData: event },
    });
  }, [event, navigate]);

  const openCompanies = useCallback(() => {
    navigate(`/b2bEvent/${event._id}/virtual-line-companies`);
  }, [event._id, navigate]);

  const openSchedules = useCallback(() => {
    onOpenSchedules(event);
  }, [event, onOpenSchedules]);

  const showBuyerSchedules = showBuyerOwnSchedulesButtonForEvent(user, event);
  const showHub = showVirtualLineCompaniesButton || showBuyerSchedules;

  return (
    <div className={styles.eventCard}>
      <AssetImage
        className={styles.eventImage}
        src={event.imagenBanner ? generateAssetUrl(event.imagenBanner) : EventBanner}
        alt={t("common.bannerAlt")}
      />
      <div className={styles.eventInfo}>
        <div className={styles.eventHeader}>
          <div className={styles.eventHeaderTitle}>
            <div className={styles.messageItemAvatar}>
              <img className={styles.avatar} src={Caintra_Logo_03} alt={t("common.avatarAlt")} />
            </div>
            <span className={styles.eventName}>{event.nombre}</span>
          </div>
          {canManageStaff && !isPastEvent(event) && (
            <div className={styles.eventHeaderActions}>
              <div className={styles.buttonContainer} onClick={editEvent}>
                <EditIcon className={styles.editButtonIcon} />
                <button className={styles.editButton}>{t("pages.b2bEvents.editEvent")}</button>
              </div>
            </div>
          )}
        </div>
        <div className={styles.eventDetails}>
          <div className={styles.eventDates}>
            <div className={styles.datesRow}>
              <EventDateColumn value={event.fechaInicioEvento} />
              <span>{t("pages.b2bEvents.to")}</span>
              <EventDateColumn value={event.fechaFinEvento} />
            </div>
          </div>
          <div className={styles.divider} />
          <div className={styles.eventDescriptionContainer}>
            <div className={styles.eventDescriptionUpper}>
              <span className={styles.eventDescription}>
                {truncateText(event.descripcion, DESCRIPTION_PREVIEW_LENGTH)}
              </span>
            </div>
            <EventHub
              t={t}
              showHub={showHub}
              showVirtualLineCompaniesButton={showVirtualLineCompaniesButton}
              showBuyerSchedules={showBuyerSchedules}
              openCompanies={openCompanies}
              openSchedules={openSchedules}
            />
          </div>
        </div>
        <div className={styles.eventAdditionalDetails}>
          {event.fechaLimiteRegistro && (
            <div className={styles.eventColumn}>
              <div className={styles.eventColumnItem}>
                <CalendarIcon className={styles.salesIcon} />
                <label className={styles.eventLabel}>
                  {t("pages.b2bEvents.dueDateRegistration")}
                </label>
              </div>
              <span className={styles.eventValue}>
                {dayjs(event.fechaLimiteRegistro).utc().format("LLL")}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default B2BEventCard;
